<template>
    <div class="ak-overlay" @click.self="close">
        <div class="ak-modal wide">
            <div class="ak-modal-head">
                <div class="ak-modal-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                </div>
                <div class="kd-title">
                    <h2>{{ keyData ? keyData.name : 'API Key' }}</h2>
                    <p v-if="keyData">{{ keyData.client_name }} · <code>bsk_live_{{ keyData.key_prefix }}…</code></p>
                </div>
                <span v-if="keyData" class="ak-badge" :class="statusClass(keyData.status)">{{ keyData.status }}</span>
                <button class="kd-x" type="button" aria-label="Close" @click="close">×</button>
            </div>

            <div class="ak-modal-body">
                <div v-if="loading" class="kd-loading">Loading...</div>
                <div v-else-if="loadError" class="ak-error">{{ loadError }}</div>
                <template v-else-if="keyData">
                    <div v-if="keyData.status === 'suspended'" class="ak-warn">
                        Suspended{{ keyData.suspended_reason ? `: ${keyData.suspended_reason}` : '' }}. Requests with this key are rejected.
                    </div>

                    <div class="kd-grid">
                        <div><span class="kd-k">Plan</span><span class="kd-v">{{ keyData.plan_name || '—' }}</span></div>
                        <div><span class="kd-k">Contact</span><span class="kd-v">{{ keyData.contact_email }}</span></div>
                        <div><span class="kd-k">Created</span><span class="kd-v">{{ fmtDateTime(keyData.created_at) }}</span></div>
                        <div><span class="kd-k">Expires</span><span class="kd-v">{{ keyData.expires_at ? fmtDate(keyData.expires_at) : 'Never' }}</span></div>
                        <div><span class="kd-k">Last used</span><span class="kd-v">{{ fmtDateTime(keyData.last_used_at) }}</span></div>
                        <div><span class="kd-k">Last IP</span><span class="kd-v">{{ keyData.last_ip || '—' }}</span></div>
                        <div><span class="kd-k">Signed mode</span><span class="kd-v">{{ keyData.signed_mode ? 'Required' : 'Off' }}</span></div>
                        <div><span class="kd-k">Today</span>
                            <span class="kd-v">{{ keyData.usage_today ? `${keyData.usage_today.tenders_served} tenders / ${keyData.usage_today.requests} requests` : '—' }}</span>
                        </div>
                        <div class="kd-wide"><span class="kd-k">IP allowlist</span>
                            <span class="kd-v">{{ keyData.ip_allowlist && keyData.ip_allowlist.length ? keyData.ip_allowlist.join(', ') : 'Any IP' }}</span>
                        </div>
                        <div class="kd-wide"><span class="kd-k">Overrides</span>
                            <span class="kd-v">
                                <template v-if="overrideList.length">
                                    <span v-for="o in overrideList" :key="o" class="ak-badge info kd-chip">{{ o }}</span>
                                </template>
                                <template v-else>None, follows the plan</template>
                            </span>
                        </div>
                    </div>

                    <ApiKeyUsageChart :rows="usage" :days="usageDays" :loading="usageLoading" @range="loadUsage" />

                    <div class="kd-actions" v-if="keyData.status !== 'revoked'">
                        <button class="ak-btn ghost" @click="editing = !editing">{{ editing ? 'Hide settings' : 'Edit settings' }}</button>
                        <button v-if="keyData.status !== 'suspended'" class="ak-btn ghost" @click="openDialog('suspend')">Suspend</button>
                        <button v-else class="ak-btn ghost" :disabled="working" @click="unsuspend">Lift suspension</button>
                        <button class="ak-btn ghost" @click="openDialog('rotate')">Rotate key</button>
                        <button class="ak-btn danger" @click="openDialog('revoke')">Revoke</button>
                    </div>
                    <p v-else class="ak-hint">This key is revoked and can no longer be changed.</p>

                    <form v-if="editing && keyData.status !== 'revoked'" class="ak-form kd-edit" @submit.prevent="saveEdit">
                        <div class="ak-row">
                            <div>
                                <label class="ak-label">Key name</label>
                                <input class="ak-input" v-model="edit.name" required>
                            </div>
                            <div>
                                <label class="ak-label">Client name</label>
                                <input class="ak-input" v-model="edit.client_name" required>
                            </div>
                        </div>
                        <div class="ak-row">
                            <div>
                                <label class="ak-label">Contact email</label>
                                <input class="ak-input" type="email" v-model="edit.contact_email" required>
                            </div>
                            <div>
                                <label class="ak-label">Plan</label>
                                <select class="ak-input" v-model.number="edit.plan_id" required>
                                    <option v-for="p in selectablePlans" :key="p.id" :value="p.id">{{ p.name }}{{ p.is_active ? '' : ' (inactive)' }}</option>
                                </select>
                            </div>
                        </div>

                        <div class="kd-ovr">
                            <span class="ak-label">Overrides</span>
                            <p class="ak-hint">
                                <strong>Inherit</strong> uses the plan, <strong>Custom</strong> sets a different value,
                                <strong>Unlimited</strong> removes the limit. Quotas count tender records returned.
                            </p>
                            <ApiLimitsEditor v-model="edit.overrides" :portals="portals" :allow-inherit="true" />
                        </div>

                        <div>
                            <label class="ak-label">IP allowlist</label>
                            <textarea class="ak-input" rows="2" v-model="edit.ipText" placeholder="One IP or CIDR per line. Empty = any IP"></textarea>
                        </div>

                        <div class="ak-row">
                            <div>
                                <label class="ak-label">Expires on</label>
                                <input class="ak-input" type="date" v-model="edit.expiresDate" :disabled="edit.never">
                                <label class="ak-check kd-never"><input type="checkbox" v-model="edit.never"> Never expires</label>
                            </div>
                            <div>
                                <label class="ak-label">Options</label>
                                <label class="ak-check"><input type="checkbox" v-model="edit.signed_mode"> Require signed requests</label>
                                <label class="ak-check kd-never"><input type="checkbox" v-model="edit.is_active"> Key is enabled</label>
                            </div>
                        </div>

                        <div v-if="editError" class="ak-error">{{ editError }}</div>
                        <div class="ak-actions">
                            <button type="button" class="ak-btn ghost" @click="editing = false">Cancel</button>
                            <button type="submit" class="ak-btn primary" :disabled="working">
                                <span v-if="working" class="ak-spinner"></span>{{ working ? 'Saving...' : 'Save Changes' }}
                            </button>
                        </div>
                    </form>
                </template>
            </div>
        </div>

        <!-- Action dialogs -->
        <div v-if="dialog" class="ak-overlay kd-dialog-overlay" @click.self="closeDialog">
            <div class="ak-modal narrow ak-confirm">
                <template v-if="dialog === 'suspend'">
                    <h3>Suspend this key?</h3>
                    <p>Requests with it are rejected until you lift the suspension.</p>
                    <input class="ak-input" v-model="reason" placeholder="Reason (optional)">
                </template>
                <template v-else-if="dialog === 'rotate'">
                    <h3>Rotate this key?</h3>
                    <p>A new key is issued. The current key keeps working for the grace period, then stops.</p>
                    <label class="ak-label left">Grace period (days, 0-30)</label>
                    <input class="ak-input" type="number" min="0" max="30" step="1" v-model.number="graceDays">
                    <label class="ak-label left kd-pw">Your password</label>
                    <input class="ak-input" type="password" v-model="password" autocomplete="current-password" placeholder="Confirm with your password">
                </template>
                <template v-else>
                    <h3>Revoke this key?</h3>
                    <p>The key stops working immediately and cannot be restored.</p>
                    <label class="ak-label left">Your password</label>
                    <input class="ak-input" type="password" v-model="password" autocomplete="current-password" placeholder="Confirm with your password">
                </template>

                <div v-if="dialogError" class="ak-error kd-dialog-error">{{ dialogError }}</div>
                <div class="ak-actions">
                    <button class="ak-btn ghost" :disabled="working" @click="closeDialog">Cancel</button>
                    <button class="ak-btn" :class="dialog === 'revoke' ? 'danger' : 'primary'" :disabled="working" @click="runDialog">
                        <span v-if="working" class="ak-spinner"></span>
                        {{ working ? 'Working...' : dialogLabel }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ApiLimitsEditor from './ApiLimitsEditor.vue';
import ApiKeyUsageChart from './ApiKeyUsageChart.vue';
import {
    authHeaders, errorMessage, makeOverrideState, overridePayload, validateState, parseIpList, invalidIps,
    describeOverrides, fmtDate, fmtDateTime,
} from './apiKeysCommon';

export default {
    name: 'ApiKeyDetail',
    components: { ApiLimitsEditor, ApiKeyUsageChart },
    props: {
        keyId: { type: Number, required: true },
        plans: { type: Array, required: true },
        portals: { type: Array, default: () => [] },
    },
    emits: ['close', 'updated', 'reveal', 'notify'],
    data() {
        return {
            keyData: null,
            loading: true,
            loadError: '',
            usage: [],
            usageDays: 7,
            usageLoading: false,
            editing: false,
            editError: '',
            edit: null,
            working: false,
            dialog: '',
            dialogError: '',
            password: '',
            graceDays: 7,
            reason: '',
        };
    },
    computed: {
        overrideList() { return describeOverrides(this.keyData && this.keyData.overrides); },
        selectablePlans() {
            return this.plans.filter(p => p.is_active || (this.keyData && p.id === this.keyData.plan_id));
        },
        dialogLabel() {
            return { suspend: 'Suspend', rotate: 'Rotate Key', revoke: 'Revoke Key' }[this.dialog] || 'Confirm';
        },
    },
    async mounted() {
        await this.load();
        this.loadUsage(7);
    },
    methods: {
        fmtDate, fmtDateTime,
        statusClass(s) {
            return { active: 'ok', expired: 'warn', revoked: 'bad', suspended: 'warn' }[s] || 'off';
        },
        close() {
            if (!this.working) this.$emit('close');
        },
        async load() {
            this.loading = true;
            this.loadError = '';
            try {
                const res = await axios.get(`${this.$API_URL}/super_admin/api-keys/${this.keyId}`, { headers: authHeaders() });
                this.keyData = res.data;
                this.resetEdit();
            } catch (err) {
                this.loadError = errorMessage(err, 'Failed to load API key');
            } finally {
                this.loading = false;
            }
        },
        async loadUsage(days) {
            this.usageDays = days;
            this.usageLoading = true;
            try {
                const res = await axios.get(`${this.$API_URL}/super_admin/api-keys/${this.keyId}/usage`, {
                    headers: authHeaders(), params: { days },
                });
                this.usage = [...(res.data.days || [])].sort((a, b) => (a.day < b.day ? -1 : 1));
            } catch (err) {
                this.usage = [];
                this.$emit('notify', { type: 'error', message: errorMessage(err, 'Failed to load usage') });
            } finally {
                this.usageLoading = false;
            }
        },
        resetEdit() {
            const k = this.keyData;
            this.edit = {
                name: k.name,
                client_name: k.client_name,
                contact_email: k.contact_email,
                plan_id: k.plan_id,
                overrides: makeOverrideState(k.overrides),
                ipText: (k.ip_allowlist || []).join('\n'),
                expiresDate: k.expires_at ? String(k.expires_at).slice(0, 10) : '',
                never: !k.expires_at,
                signed_mode: !!k.signed_mode,
                is_active: !!k.is_active,
            };
            this.editError = '';
        },
        async saveEdit() {
            if (this.working) return;
            this.editError = '';
            const e = this.edit;
            if (!e.name.trim() || !e.client_name.trim()) { this.editError = 'Key name and client name are required'; return; }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.contact_email)) { this.editError = 'Enter a valid contact email'; return; }
            const problem = validateState(e.overrides);
            if (problem) { this.editError = problem; return; }
            const ips = parseIpList(e.ipText);
            const bad = invalidIps(ips);
            if (bad.length) { this.editError = `Invalid IP/CIDR: ${bad.join(', ')}`; return; }
            if (!e.never && !e.expiresDate) { this.editError = 'Pick an expiry date or tick Never expires'; return; }

            const body = {
                name: e.name.trim(),
                client_name: e.client_name.trim(),
                contact_email: e.contact_email.trim(),
                plan_id: e.plan_id,
                overrides: overridePayload(e.overrides),
                ip_allowlist: ips,
                signed_mode: e.signed_mode,
                is_active: e.is_active,
            };
            const oldDate = this.keyData.expires_at ? String(this.keyData.expires_at).slice(0, 10) : '';
            if (e.never) {
                if (this.keyData.expires_at) body.expires_at = null;
            } else if (e.expiresDate !== oldDate) {
                body.expires_at = `${e.expiresDate}T23:59:59`;
            }

            this.working = true;
            try {
                const res = await axios.patch(`${this.$API_URL}/super_admin/api-keys/${this.keyId}`, body, { headers: authHeaders() });
                this.keyData = res.data;
                this.resetEdit();
                this.editing = false;
                this.$emit('notify', { type: 'success', message: 'API key updated' });
                this.$emit('updated');
            } catch (err) {
                this.editError = errorMessage(err, 'Failed to update API key');
            } finally {
                this.working = false;
            }
        },
        openDialog(name) {
            this.dialog = name;
            this.dialogError = '';
            this.password = '';
            this.reason = '';
            this.graceDays = 7;
        },
        closeDialog() {
            if (!this.working) { this.dialog = ''; this.password = ''; }
        },
        async unsuspend() {
            if (this.working) return;
            this.working = true;
            try {
                const res = await axios.patch(`${this.$API_URL}/super_admin/api-keys/${this.keyId}`, { suspend: false }, { headers: authHeaders() });
                this.keyData = res.data;
                this.resetEdit();
                this.$emit('notify', { type: 'success', message: 'Suspension lifted' });
                this.$emit('updated');
            } catch (err) {
                this.$emit('notify', { type: 'error', message: errorMessage(err, 'Failed to lift suspension') });
            } finally {
                this.working = false;
            }
        },
        async runDialog() {
            if (this.working) return;
            this.dialogError = '';
            const base = `${this.$API_URL}/super_admin/api-keys/${this.keyId}`;
            if (this.dialog !== 'suspend' && !this.password) { this.dialogError = 'Enter your password to confirm'; return; }
            if (this.dialog === 'rotate' && !(Number.isInteger(this.graceDays) && this.graceDays >= 0 && this.graceDays <= 30)) {
                this.dialogError = 'Grace period must be a whole number from 0 to 30';
                return;
            }
            this.working = true;
            try {
                if (this.dialog === 'suspend') {
                    const body = { suspend: true };
                    if (this.reason.trim()) body.suspended_reason = this.reason.trim();
                    const res = await axios.patch(base, body, { headers: authHeaders() });
                    this.keyData = res.data;
                    this.resetEdit();
                    this.$emit('notify', { type: 'success', message: 'API key suspended' });
                } else if (this.dialog === 'rotate') {
                    const res = await axios.post(`${base}/rotate`, { password: this.password, grace_days: this.graceDays }, { headers: authHeaders() });
                    this.password = '';
                    this.dialog = '';
                    this.$emit('updated');
                    this.$emit('reveal', {
                        title: 'New API key issued',
                        name: res.data.key ? res.data.key.name : this.keyData.name,
                        api_key: res.data.api_key,
                        signing_secret: res.data.signing_secret || null,
                    });
                    return;
                } else {
                    await axios.post(`${base}/revoke`, { password: this.password }, { headers: authHeaders() });
                    this.$emit('notify', { type: 'success', message: 'API key revoked' });
                    this.$emit('updated');
                    this.password = '';
                    this.dialog = '';
                    this.$emit('close');
                    return;
                }
                this.dialog = '';
                this.$emit('updated');
            } catch (err) {
                this.dialogError = errorMessage(err, 'Action failed');
            } finally {
                this.working = false;
            }
        },
    },
};
</script>

<style scoped>
.kd-title { flex: 1; min-width: 0; }
.kd-title code { font-size: 0.78rem; }
.kd-x {
    border: none; background: transparent; font-size: 1.6rem; line-height: 1; color: #94a3b8; cursor: pointer; padding: 0 0.25rem;
}
.kd-x:hover { color: var(--text-primary); }
.kd-loading { padding: 2rem; text-align: center; color: #94a3b8; }
.kd-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem 1.25rem; }
.kd-grid > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.kd-wide { grid-column: 1 / -1; }
.kd-k { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-label); font-weight: 700; }
.kd-v { font-size: 0.88rem; color: var(--text-primary); font-weight: 600; word-break: break-word; }
.kd-chip { margin: 2px 4px 2px 0; }
.kd-actions { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.kd-edit { border-top: 1px solid #e2e8f0; padding-top: 1.25rem; }
.kd-ovr { display: flex; flex-direction: column; gap: 0.5rem; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 1rem; }
.kd-ovr .ak-hint { margin: 0; }
.kd-never { margin-top: 0.5rem; }
.kd-pw { margin-top: 0.75rem; }
.kd-dialog-overlay { z-index: 1100; }
.kd-dialog-error { margin-top: 0.75rem; }
@media (max-width: 640px) { .kd-grid { grid-template-columns: 1fr; } }
</style>
